import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowUpRight, MapPin, Wifi, KeyRound, Coffee, Printer, ShieldCheck,
  Clock, Plus, Minus, Star, Quote, Building2
} from "lucide-react";
import { FeedbackForm } from "../components/FeedbackForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMG = "/img/landing_hero_building.jpg";

const SERVICE_CARDS = [
  { to: "/boardrooms", testId: "service-card-boardrooms", eyebrow: "Service 01",
    title: "Boardroom Hire", locations: "Toowoomba & Sunshine Coast",
    description: "Three boardrooms across two locations. Book by the hour, half-day or full day — meetings, workshops, and quiet focused sessions.",
    imgUrl: "/img/sketch_boardroom.png" },
  { to: "/hot-desking", testId: "service-card-hot-desking", eyebrow: "Service 02",
    title: "Hot Desk Hire", locations: "Toowoomba",
    description: "A warm, quiet desk to call your own — fully kitted out. Bring your laptop and charger, we've got the rest.",
    imgUrl: "/img/sketch_hotdesk.png" },
  { to: "/leasing", testId: "service-card-leasing", eyebrow: "Service 03",
    title: "Tenant Leasing", locations: "Toowoomba",
    description: "Light-filled commercial spaces in the heart of the Toowoomba CBD — ready for your team to move in.",
    imgUrl: "/img/sketch_leasing.png" },
];

const STATS = [
  { value: "3", label: "Boardrooms" },
  { value: "2", label: "Locations" },
  { value: "10+", label: "Hot Desks" },
];

const BENEFITS = [
  { icon: KeyRound, title: "Secure 24/7 access", body: "Coded entry plus your own key — drop in whenever you need to work." },
  { icon: Wifi,     title: "Separate guest Wi-Fi", body: "Fast, dedicated Wi-Fi for guests so your team's network stays private." },
  { icon: Coffee,   title: "Coffee & pantry on us", body: "Espresso machine, pantry stocked with the essentials. No need to step out." },
  { icon: Printer,  title: "Printer + stationery",  body: "Pens, pads and a full-feature printer — sit down and start working." },
  { icon: ShieldCheck, title: "No long lock-ins",   body: "Month-to-month memberships. Scale up or down as your team changes." },
  { icon: MapPin,   title: "Two great locations",  body: "Toowoomba CBD or Buderim on the Sunshine Coast — choose whichever fits your day." },
];

const TestimonialCard = ({ t }) => (
  <figure className="bg-[#F9F6F0] border border-[#E6DFD3] p-7 sm:p-8 rounded-sm">
    <Quote size={22} strokeWidth={1.5} className="text-[#8B7355] mb-4" />
    <blockquote className="font-serif text-lg sm:text-xl italic text-[#2D231E] leading-snug mb-6">
      "{t.message}"
    </blockquote>
    <figcaption>
      <p className="font-sans text-sm text-[#2D231E]">{t.name}</p>
      {t.role && (
        <p className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#8B7355] mt-0.5">{t.role}</p>
      )}
    </figcaption>
  </figure>
);

const FAQS = [
  { q: "Do I need to sign a lock-in contract?", a: "No. All hot desk and leasing arrangements are flexible — month-to-month with no long-term commitment unless you want one." },
  { q: "How do I get into the building?",       a: "It depends on what you've booked. For hot desks and tenant leasing, once your booking is confirmed we issue you a secure coded entry PIN plus a key so you can come and go 24/7. For boardroom hire, one of our team will be onsite to meet you, let you in, and get you set up for your meeting." },
  { q: "What's included with a hot desk?",      a: "1 monitor, internet cable, keyboard, mouse, camera, headphones, lockable cupboard storage, docking station, HDMI cables, separate Wi-Fi, printer & stationery. Just bring your laptop and charger." },
  { q: "Can I tour the space before booking?",  a: "Absolutely — send an enquiry from any of the service pages and we'll arrange a walk-through at a time that suits you." },
];

const ServiceCard = ({ card }) => (
  <Link to={card.to} data-testid={card.testId}
    className="group block w-full bg-[#FAF9F6] border border-[#E6DFD3] rounded-sm overflow-hidden transition-colors duration-300 hover:border-[#8B7355]">
    <div className="relative aspect-[4/3] overflow-hidden bg-[#F2EBDC]">
      <img src={card.imgUrl} alt={card.title}
        className="w-full h-full object-contain transition-transform duration-700 ease-in-out group-hover:scale-105" loading="lazy" />
      <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[#F9F6F0]/90 backdrop-blur-sm px-3 py-1.5 rounded-sm">
        <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]">{card.locations}</span>
      </div>
    </div>
    <div className="p-6 sm:p-7">
      <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#8B7355] mb-2">{card.eyebrow}</p>
      <h3 className="font-serif text-2xl text-[#2D231E] mb-3 group-hover:text-[#8B7355] transition-colors leading-tight">{card.title}</h3>
      <p className="font-sans text-sm leading-relaxed text-[#5D4037] mb-5">{card.description}</p>
      <span className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.2em] uppercase text-[#4A3C31] group-hover:text-[#2D231E]">
        Step inside
        <ArrowUpRight size={14} strokeWidth={1.5}
          className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </div>
  </Link>
);

const Faq = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-[#E6DFD3]">
      <button type="button" onClick={() => setOpen(!open)}
        data-testid={`faq-${idx}`}
        className="w-full flex items-center justify-between py-5 sm:py-6 text-left">
        <span className="font-serif text-lg sm:text-xl text-[#2D231E] pr-6">{q}</span>
        {open ? <Minus size={18} strokeWidth={1.5} className="text-[#8B7355] shrink-0" />
              : <Plus  size={18} strokeWidth={1.5} className="text-[#8B7355] shrink-0" />}
      </button>
      {open && (
        <div className="pb-6 -mt-1 max-w-3xl">
          <p className="font-sans text-sm sm:text-base leading-relaxed text-[#5D4037]">{a}</p>
        </div>
      )}
    </div>
  );
};

export default function LandingPage() {
  const [reviews, setReviews] = useState([]);
  const [reviewsLoaded, setReviewsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/feedback/public`)
      .then((res) => { if (!cancelled) setReviews(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setReviews([]); })
      .finally(() => { if (!cancelled) setReviewsLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  const hasReviews = reviews.length > 0;
  return (
    <main data-testid="landing-page" className="animate-in fade-in duration-500">

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 sm:pt-24 pb-16 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <p data-testid="hero-eyebrow" className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-6">
              Two Locations, Three Services
            </p>
            <h1 data-testid="hero-title" className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#2D231E] leading-[1.02] mb-6">
              Work<span className="italic text-[#8B7355]">Spaces</span>
            </h1>
            <p data-testid="hero-tagline" className="font-serif text-2xl sm:text-3xl italic text-[#4A3C31] leading-snug max-w-xl mb-6">
              A quiet place for your best work.
            </p>
            <p className="font-sans text-base sm:text-lg leading-relaxed text-[#5D4037] max-w-lg mb-8">
              In the heart of the Toowoomba CBD and on the Sunshine Coast.
              Boardrooms, hot desk space and commercial office leasing.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#services" className="inline-flex items-center justify-center px-7 py-3.5 bg-[#4A3C31] text-[#F9F6F0] font-sans text-xs tracking-[0.3em] uppercase hover:bg-[#2D231E] transition-colors rounded-sm">
                See What We Offer
              </a>
              <Link to="/hot-desking#enquire" className="inline-flex items-center justify-center px-7 py-3.5 bg-transparent border border-[#4A3C31] text-[#4A3C31] font-sans text-xs tracking-[0.3em] uppercase hover:bg-[#4A3C31] hover:text-[#F9F6F0] transition-colors rounded-sm">
                Book a Tour
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-[#E6DFD3]">
              <img src={HERO_IMG} alt="A calm, warm workspace" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D231E]/15 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section data-testid="stats-strip" className="border-y border-[#E6DFD3] bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-10 sm:py-12 grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 text-center">
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-serif text-4xl sm:text-5xl text-[#2D231E] mb-2">{s.value}</p>
              <p className="font-sans text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-[#8B7355]">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" id="services" className="max-w-7xl mx-auto px-6 sm:px-12 pt-20 sm:pt-24 pb-20 sm:pb-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12 sm:mb-14">
          <div>
            <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-3">What you can do here</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D231E]">Three ways in.</h2>
          </div>
          <p className="font-sans text-sm sm:text-base text-[#5D4037] max-w-sm">
            Pick what you need — book a room, take a desk for the day, or move your team in for the long term.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {SERVICE_CARDS.map((c) => <ServiceCard key={c.testId} card={c} />)}
        </div>
      </section>

      {/* BENEFITS */}
      <section data-testid="benefits-section" className="border-t border-[#E6DFD3]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-24">
          <div className="max-w-2xl mb-12 sm:mb-14">
            <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-3">Why WorkSpaces?</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D231E] mb-4">
              Flexibility, location options, and everything you'd expect in a great workspace.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {BENEFITS.map((b) => {
              const Icon = b.icon;
              return (
                <div key={b.title}>
                  <div className="w-10 h-10 mb-5 flex items-center justify-center border border-[#E6DFD3] rounded-sm bg-[#FAF9F6]">
                    <Icon size={18} strokeWidth={1.5} className="text-[#5D4037]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#2D231E] mb-2.5">{b.title}</h3>
                  <p className="font-sans text-sm leading-relaxed text-[#5D4037] max-w-sm">{b.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — only render the reviews grid when we have approved reviews.
          The feedback form is always shown so visitors can submit theirs. */}
      <section data-testid="testimonials-section" className="border-t border-[#E6DFD3] bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-24">
          {hasReviews && (
            <>
              <div className="flex items-end justify-between flex-wrap gap-6 mb-12 sm:mb-14">
                <div>
                  <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-3">In their words</p>
                  <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D231E]">
                    Quiet conversations, real work done.
                  </h2>
                </div>
                <div className="flex items-center gap-2" data-testid="testimonials-stars">
                  {[1,2,3,4,5].map(i => <Star key={i} size={16} strokeWidth={1.5} className="text-[#8B7355] fill-[#8B7355]" />)}
                  <span className="font-sans text-xs tracking-[0.2em] uppercase text-[#5D4037] ml-2">
                    {reviews.length} review{reviews.length === 1 ? "" : "s"}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" data-testid="testimonials-grid">
                {reviews.slice(0, 6).map((t) => <TestimonialCard key={t.id} t={t} />)}
              </div>
            </>
          )}

          {!hasReviews && reviewsLoaded && (
            <div className="max-w-2xl mb-12 sm:mb-14" data-testid="testimonials-empty">
              <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-3">In their words</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D231E] mb-4">
                We'd love to hear from you.
              </h2>
              <p className="font-sans text-base text-[#5D4037]">
                If you've spent time with us — meeting, working or visiting — share a short word below.
              </p>
            </div>
          )}

          <div className={`${hasReviews ? "mt-14 sm:mt-16" : ""} max-w-3xl mx-auto`}>
            <FeedbackForm />
          </div>
        </div>
      </section>

      {/* LOCATION STRIP */}
      <section data-testid="location-strip" className="border-t border-[#E6DFD3]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-3">Find us</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D231E] mb-5">
              Two location options.
            </h2>
            <p className="font-sans text-base text-[#5D4037] mb-6 max-w-lg">
              Heart of the Toowoomba CBD and Buderim on the Sunshine Coast —
              both quiet, both warm, both ready when you are.
            </p>
            <div className="flex flex-col gap-3 mb-6">
              <div className="flex items-start gap-3">
                <Building2 size={16} strokeWidth={1.5} className="text-[#8B7355] mt-1 shrink-0" />
                <div>
                  <p className="font-serif text-base text-[#2D231E]">Saba Workspace — Toowoomba</p>
                  <p className="font-sans text-sm text-[#5D4037]">Level 1, 469 Ruthven Street, Toowoomba City</p>
                  <p className="font-sans text-xs text-[#8B7355] mt-0.5">Next to Grand Central · Parking not included</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Building2 size={16} strokeWidth={1.5} className="text-[#8B7355] mt-1 shrink-0" />
                <div>
                  <p className="font-serif text-base text-[#2D231E]">Buderim Workspace — Sunshine Coast</p>
                  <p className="font-sans text-sm text-[#5D4037]">52 Burnett Street, Buderim</p>
                  <p className="font-sans text-xs text-[#8B7355] mt-0.5">Underground parking on Level 1 if required</p>
                </div>
              </div>
              <span className="inline-flex items-center gap-3 font-sans text-sm text-[#5D4037] mt-1">
                <Clock size={16} strokeWidth={1.5} className="text-[#8B7355]" />
                Secure 24/7 access — coded entry + key on arrival
              </span>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[5/3] overflow-hidden rounded-sm border border-[#E6DFD3] bg-[#FAF9F6]">
              <img src="/img/sketch_leasing.png" alt="A look inside the Toowoomba workspace"
                className="w-full h-full object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="faq-section" className="border-t border-[#E6DFD3] bg-[#FAF9F6]">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 py-20 sm:py-24">
          <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-3">Frequently asked</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D231E] mb-10">
            Good questions, quick answers.
          </h2>
          <div>
            {FAQS.map((f, i) => <Faq key={f.q} q={f.q} a={f.a} idx={i} />)}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section data-testid="closing-cta" className="border-t border-[#E6DFD3]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-20 sm:py-24 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D231E] mb-5">
            Come see the space.
          </h2>
          <p className="font-sans text-base sm:text-lg text-[#5D4037] max-w-xl mx-auto mb-8">
            Send an enquiry from any of the service pages — we'll be in touch to
            arrange a walk-through at a time that suits.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/boardrooms" className="inline-flex items-center justify-center px-7 py-3.5 bg-[#4A3C31] text-[#F9F6F0] font-sans text-xs tracking-[0.3em] uppercase hover:bg-[#2D231E] transition-colors rounded-sm">Book a Boardroom</Link>
            <Link to="/hot-desking" className="inline-flex items-center justify-center px-7 py-3.5 bg-transparent border border-[#4A3C31] text-[#4A3C31] font-sans text-xs tracking-[0.3em] uppercase hover:bg-[#4A3C31] hover:text-[#F9F6F0] transition-colors rounded-sm">Hire a Hot Desk</Link>
            <Link to="/leasing" className="inline-flex items-center justify-center px-7 py-3.5 bg-transparent border border-[#4A3C31] text-[#4A3C31] font-sans text-xs tracking-[0.3em] uppercase hover:bg-[#4A3C31] hover:text-[#F9F6F0] transition-colors rounded-sm">Tour a Tenancy</Link>
          </div>
        </div>
      </section>

    </main>
  );
}
